import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'

const FOCUSABLE = 'button, a[href], input, select, textarea, [tabindex], [contenteditable="true"]'

/** A portalled popup belongs to the dialog when a control inside it names the popup. */
function dialogRoots(dialog: HTMLElement): HTMLElement[] {
  const roots = [dialog]
  for (let i = 0; i < roots.length; i++) {
    for (const control of roots[i].querySelectorAll('[aria-controls]')) {
      for (const id of (control.getAttribute('aria-controls') ?? '').split(/\s+/)) {
        const popup = document.getElementById(id)
        if (popup && !roots.includes(popup) && !roots.some((root) => root.contains(popup))) roots.push(popup)
      }
    }
  }
  return roots
}

const OVERLAY = '[role="dialog"], [role="alertdialog"], [aria-modal="true"]'

/**
 * Focus sits in another overlay that is not ours and not portalled from us: a tour step
 * (react-joyride renders role="alertdialog"), a lightbox over the modal. That overlay owns the
 * keyboard; pulling focus back would make Enter close the modal instead of pressing «Далее».
 */
function inForeignOverlay(node: EventTarget | null, roots: HTMLElement[]): boolean {
  if (!(node instanceof Element)) return false
  const overlay = node.closest(OVERLAY)
  // Our own Modal/Drawer stack is ordered by isTopmost(); only outside overlays are exempt.
  if (!overlay || overlay.closest('[data-pim-dialog]')) return false
  return !roots.some((root) => root === overlay || root.contains(overlay))
}

function available(node: HTMLElement): boolean {
  if (node.tabIndex < 0 || node.matches(':disabled') || node.closest('[hidden], [inert]')) return false
  for (let at: HTMLElement | null = node; at; at = at.parentElement) {
    const style = getComputedStyle(at)
    if (style.display === 'none' || style.visibility === 'hidden') return false
  }
  return true
}

/** Focus belongs to the top overlay; popup Escape handlers keep their existing ownership. */
export function useDialogFocus(open: boolean, ref: RefObject<HTMLElement>, isTopmost: () => boolean): void {
  const wasOpen = useRef(false)
  const opener = useRef<HTMLElement | null>(null)
  // Capture before React commits children with autoFocus, which can otherwise replace the opener.
  if (open && !wasOpen.current && typeof document !== 'undefined') {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
  }
  wasOpen.current = open
  useEffect(() => {
    const dialog = ref.current
    if (!open || !dialog) return
    const previous = opener.current
    const roots = () => dialogRoots(dialog)
    const focusable = () => Array.from(new Set(roots().flatMap((root) => Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))))).filter(available)
    const focusInside = () => (focusable()[0] ?? dialog).focus({ preventScroll: true })
    const outside = (node: EventTarget | null) => {
      const current = roots()
      return !current.some((root) => root.contains(node as Node)) && !inForeignOverlay(node, current)
    }
    if (isTopmost() && outside(document.activeElement)) focusInside()

    const onFocus = (event: FocusEvent) => {
      if (isTopmost() && outside(event.target)) focusInside()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented || !isTopmost()) return
      if (inForeignOverlay(document.activeElement, roots())) return
      const targets = focusable()
      const index = targets.indexOf(document.activeElement as HTMLElement)
      const next = targets.length ? targets[(index + (event.shiftKey ? -1 : 1) + targets.length) % targets.length] : null
      const allowedRoots = roots()
      const rootOf = (node: Element | null) => allowedRoots.findIndex((root) => root.contains(node))
      const crossesPortal = next && rootOf(next) !== rootOf(document.activeElement)
      if (!targets.length || index === -1 || crossesPortal || (event.shiftKey ? index === 0 : index === targets.length - 1)) {
        event.preventDefault()
        const target = index === -1 ? (event.shiftKey ? targets[targets.length - 1] : targets[0]) : next
        ;(target ?? dialog).focus({ preventScroll: true })
      }
    }
    document.addEventListener('focusin', onFocus)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('focusin', onFocus)
      document.removeEventListener('keydown', onKey)
      // Do not move focus out of an overlay opened after this one, or back to a removed trigger.
      const active = document.activeElement
      if (previous?.isConnected && (active === document.body || roots().some((root) => root.contains(active)))) {
        previous.focus({ preventScroll: true })
      }
    }
  }, [open, ref, isTopmost])
}

/** Non-modal toolbar popovers keep normal Tab order and stay inside the viewport. */
export function useDisclosureFocus(open: boolean, panelRef: RefObject<HTMLElement>, preferAbove = false): { left: number; above: boolean; maxHeight?: number } {
  const [position, setPosition] = useState<{ left: number; above: boolean; maxHeight?: number }>({ left: 0, above: preferAbove })
  useLayoutEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (!panel) return
    const place = () => {
      const root = panel.parentElement?.getBoundingClientRect()
      if (!root) return
      const below = Math.max(0, window.innerHeight - root.bottom - 12)
      const aboveSpace = Math.max(0, root.top - 12)
      const desiredHeight = Math.min(panel.scrollHeight || panel.getBoundingClientRect().height, 320)
      const above = preferAbove ? aboveSpace >= desiredHeight || aboveSpace > below : below < desiredHeight && aboveSpace > below
      setPosition({
        left: Math.min(0, window.innerWidth - 8 - root.left - panel.getBoundingClientRect().width),
        above,
        maxHeight: Math.max(40, above ? aboveSpace : below),
      })
    }
    place()
    const first = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).find(available)
    ;(first ?? panel).focus({ preventScroll: true })
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, panelRef, preferAbove])
  return position
}
